import { APIContext } from "../../types";
import { makePath } from "../../../../api/url";
import * as APITypes from "../../../../api/cur8/generated-types/v1/Api";
import { applyPagination } from "../../pagination";
import { getJSON, postJSON, putJSON } from "../../request";
import { applyParams } from "../../../params";

export type AnnotationAPI = ReturnType<typeof createAnnotationAPI>;

type QueryParams =
  | APITypes.AnnotationListByPatientParams
  | APITypes.AnnotationListParams;

const DEFAULT_QUERY_PARAMS = {
  pageSize: 25,
};

function applyOptions(params: URLSearchParams, options: QueryParams) {
  applyParams(params, {
    targetUri: options.targetUri,
    physicalArtefactId:
      "physicalArtefactId" in options ? options.physicalArtefactId : undefined,
    acceptState: options.acceptState,
    applicationSpecificTarget: options.applicationSpecificTarget,
    classificationType: options.classificationType,
    dataType: options.dataType,
    minSize: options.minSize,
  });
}

export function createAnnotationAPI({
  buildURL,
  fetchJSON,
  fetchPagedJSON,
  fetchRaw,
}: APIContext) {
  function queryAnnotations(options: QueryParams) {
    const parts = ["api", "v1", "annotation"];
    if ("patientId" in options) {
      parts.push(options.patientId);
    }

    const path = makePath(parts);

    const url = buildURL(path);

    const effectiveOptions = {
      ...DEFAULT_QUERY_PARAMS,
      ...options,
    };
    applyOptions(url.searchParams, effectiveOptions);
    applyPagination(url.searchParams, effectiveOptions);

    const request = getJSON(url);
    return fetchPagedJSON<APITypes.Annotation>(request);
  }

  return {
    queryAnnotations,

    createAnnotation(
      {
        patientId,
      }: {
        patientId: string;
      },
      annotation: APITypes.CreateAnnotationRequest
    ) {
      const path = makePath(["api", "v1", "annotation", patientId]);
      const url = buildURL(path);
      const request = postJSON(url, annotation);
      return fetchJSON<APITypes.Annotation>(request);
    },

    replaceAnnotation(
      {
        patientId,
        annotationId,
      }: {
        patientId: string;
        annotationId: string;
      },
      annotation: APITypes.CreateAnnotationRequest
    ) {
      const path = makePath([
        "api",
        "v1",
        "annotation",
        patientId,
        annotationId,
      ]);

      const url = buildURL(path);

      const request = putJSON(url, annotation);

      return fetchJSON<APITypes.Annotation>(request);
    },

    deleteAnnotation({
      patientId,
      annotationId,
    }: {
      patientId: string;
      annotationId: string;
    }) {
      const path = makePath([
        "api",
        "v1",
        "annotation",
        patientId,
        annotationId,
      ]);

      const url = buildURL(path);

      const request = new Request(url.toString(), {
        method: "DELETE",
      });

      return fetchRaw(request);
    },

    getAnnotation({
      patientId,
      annotationId,
    }: {
      patientId: string;
      annotationId: string;
    }) {
      const path = makePath([
        "api",
        "v1",
        "annotation",
        patientId,
        annotationId,
      ]);

      const url = buildURL(path);

      const request = new Request(url.toString());

      return fetchJSON<APITypes.Annotation>(request);
    },
    getAnnotationArtefactCount({
      patientId,
      acceptState,
      applicationSpecificTarget,
    }: {
      patientId: string;
      acceptState?: "proposed" | "accepted" | "rejected";
      applicationSpecificTarget?: string;
    }) {
      const path = makePath([
        "api",
        "v1",
        "annotation",
        patientId,
        "artefact",
        "count",
      ]);
      const url = buildURL(path);

      applyParams(url.searchParams, {
        acceptState,
        applicationSpecificTarget,
      });

      const request = new Request(url.toString());

      return fetchJSON<number>(request);
    },
  };
}
